import { axiosClient } from '../../../../utils/axiosClient';
import { makeApiRequest, type ApiResponse } from '../../../utils';
import { DonorEndpoints } from './donorEndpoints';
import type { GiftHistoryRequest, UpdateProfileRequest } from './requests';
import type {
    GetGiftByUUIDResponse,
    GetGiftSummaryResponse,
    GetProfileResponse,
    GetRecurringDonationsResponse,
    GiftHistoryDonorResponse,
    ReceiptResponse,
} from './response';

export class DonorService {
    public async getProfile(): Promise<ApiResponse<GetProfileResponse>> {
        const url = DonorEndpoints.getProfile();
        const httpRequest = axiosClient.get(url);
        return makeApiRequest<GetProfileResponse>(httpRequest);
    }

    public async updateProfile(
        payload: Partial<UpdateProfileRequest>,
    ): Promise<ApiResponse<any>> {
        const url = DonorEndpoints.updateProfile();
        const httpRequest = axiosClient.put(url, payload);
        const result = await makeApiRequest<any>(httpRequest);

        if (!result.success) {
            return result;
        }

        const responseCode = result.data?.response?.code;
        if (
            responseCode !== 200 &&
            responseCode !== 201 &&
            responseCode !== 204
        ) {
            return {
                success: false,
                error: {
                    isAxiosError: false,
                    status: responseCode || 500,
                    errors: result.data?.response?.errors,
                    message:
                        result.data?.response?.error_message ||
                        result.data?.response?.errorMessage,
                    name: String(responseCode || 500),
                },
            };
        }

        return result;
    }

    public async getGiftByUUID(
        giftUUID: string,
    ): Promise<ApiResponse<GetGiftByUUIDResponse>> {
        const url = DonorEndpoints.getGiftByUUID(giftUUID);
        const httpRequest = axiosClient.get(url);
        const result = await makeApiRequest<GetGiftByUUIDResponse>(httpRequest);
        return result;
    }

    public async getReceiptRequest(
        receiptId: number | string,
    ): Promise<ApiResponse<ReceiptResponse>> {
        const url = DonorEndpoints.getReceipt(receiptId);
        const httpRequest = axiosClient.get(url);
        const result = await makeApiRequest<ReceiptResponse>(httpRequest);
        return result;
    }

    public async getGiftHistory(
        data: GiftHistoryRequest,
    ): Promise<ApiResponse<GiftHistoryDonorResponse>> {
        const url = DonorEndpoints.getGiftHistory(data);
        const httpRequest = axiosClient.get(url);
        const result =
            await makeApiRequest<GiftHistoryDonorResponse>(httpRequest);
        return result;
    }

    public async getGiftSummary(): Promise<
        ApiResponse<GetGiftSummaryResponse>
    > {
        const url = DonorEndpoints.getGiftSummary();
        const httpRequest = axiosClient.get(url);
        const result =
            await makeApiRequest<GetGiftSummaryResponse>(httpRequest);
        return result;
    }

    public async setPreferredWallet(
        walletId: number,
    ): Promise<ApiResponse<unknown>> {
        const url = DonorEndpoints.setPreferredWallet(walletId);
        const httpRequest = axiosClient.put(url);
        return makeApiRequest<unknown>(httpRequest);
    }

    public async deleteWallet(walletId: number): Promise<ApiResponse<unknown>> {
        const url = DonorEndpoints.deleteWallet(walletId);
        const httpRequest = axiosClient.delete(url);
        return makeApiRequest<unknown>(httpRequest);
    }

    public async getAllRecurringDonations(): Promise<
        ApiResponse<GetRecurringDonationsResponse>
    > {
        const url = DonorEndpoints.getAllRecurringDonations();
        const httpRequest = axiosClient.get(url);
        return makeApiRequest<GetRecurringDonationsResponse>(httpRequest);
    }

    public async cancelRecurringDonation(
        recurringId: number,
    ): Promise<ApiResponse<unknown>> {
        const url = DonorEndpoints.cancelRecurringDonation(recurringId);
        const httpRequest = axiosClient.delete(url);
        return makeApiRequest<unknown>(httpRequest);
    }
}
